import { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import { getInitials, getCompanyInitials } from '@/utils/initials'

interface PersonAvatarProps {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  profilePicUrl?: string | null
  alt?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}

interface CompanyAvatarProps {
  companyName: string
  logoUrl?: string | null
  alt?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}

const sizeClasses = {
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-16 h-16 text-base',
  '2xl': 'w-20 h-20 text-lg'
}

/**
 * Avatar component for displaying a person's profile picture or initials.
 * Handles image load errors by falling back to initials.
 */
export const PersonAvatar: FunctionalComponent<PersonAvatarProps> = ({
  firstName,
  lastName,
  fullName,
  profilePicUrl,
  alt,
  size = 'md'
}) => {
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(profilePicUrl ? 'loading' : 'error')
  const initials = getInitials({ firstName, lastName, fullName })
  const displayName = alt || fullName || [firstName, lastName].filter(Boolean).join(' ') || 'User'
  const sizeClass = sizeClasses[size]

  const handleImageLoad = () => {
    setImageStatus('loaded')
  }

  const handleImageError = () => {
    setImageStatus('error')
  }

  return (
    <div
      class={`${sizeClass} rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center font-semibold text-gray-600 dark:text-gray-300 relative`}
      title={displayName}
    >
      {/* Show initials when no image URL or image failed to load */}
      {(!profilePicUrl || imageStatus === 'error') && (
        <span>{initials}</span>
      )}
      
      {/* Load image when URL exists */}
      {profilePicUrl && (
        <img
          src={profilePicUrl}
          alt={displayName}
          class="w-full h-full object-cover absolute inset-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ opacity: imageStatus === 'loaded' ? 1 : 0 }}
        />
      )}
    </div>
  )
}

/**
 * Avatar component for displaying a company logo or initials.
 * Handles image load errors by falling back to initials.
 */
export const CompanyAvatar: FunctionalComponent<CompanyAvatarProps> = ({
  companyName,
  logoUrl,
  alt,
  size = 'md'
}) => {
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(logoUrl ? 'loading' : 'error')
  const initials = getCompanyInitials(companyName)
  const displayName = alt || companyName
  const sizeClass = sizeClasses[size]

  const handleImageLoad = () => {
    setImageStatus('loaded')
  }

  const handleImageError = () => {
    setImageStatus('error')
  }

  return (
    <div
      class={`${sizeClass} rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center font-semibold text-gray-600 dark:text-gray-300 relative`}
      title={displayName}
    >
      {/* Show initials when no logo URL or image failed to load */}
      {(!logoUrl || imageStatus === 'error') && (
        <span>{initials}</span>
      )}
      
      {/* Load image when URL exists */}
      {logoUrl && (
        <img
          src={logoUrl}
          alt={displayName}
          class="w-full h-full object-cover absolute inset-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ opacity: imageStatus === 'loaded' ? 1 : 0 }}
        />
      )}
    </div>
  )
}
